/**
 * Invoice API Client
 * Phase 2: Wholesale Partnership Module
 */

import apiClient from '../client';

const BASE_URL = '/wholesale/invoices';

// ===== INVOICE MANAGEMENT =====

/**
 * Get all invoices with optional filters
 */
export const getAllInvoices = async (params = {}) => {
  try {
    const response = await apiClient.get(BASE_URL, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch invoices',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get invoice by ID
 */
export const getInvoiceById = async (id) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching invoice:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch invoice',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Create invoice from order
 */
export const createInvoice = async (orderData) => {
  try {
    const response = await apiClient.post(BASE_URL, orderData);
    return response.data;
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw {
      message: error.response?.data?.error || 'Failed to create invoice',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Update invoice status
 */
export const updateInvoiceStatus = async (id, statusData) => {
  try {
    const response = await apiClient.patch(`${BASE_URL}/${id}/status`, statusData);
    return response.data;
  } catch (error) {
    console.error('Error updating invoice status:', error);
    throw {
      message: error.response?.data?.error || 'Failed to update invoice status',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Record payment for invoice
 */
export const recordInvoicePayment = async (id, paymentData) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/payments`, paymentData);
    return response.data;
  } catch (error) {
    console.error('Error recording invoice payment:', error);
    throw {
      message: error.response?.data?.error || 'Failed to record invoice payment',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Send payment reminder
 */
export const sendPaymentReminder = async (id, reminderData) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/reminders`, reminderData);
    return response.data;
  } catch (error) {
    console.error('Error sending payment reminder:', error);
    throw {
      message: error.response?.data?.error || 'Failed to send payment reminder',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Mark invoice as viewed
 */
export const markInvoiceAsViewed = async (id) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/view`);
    return response.data;
  } catch (error) {
    console.error('Error marking invoice as viewed:', error);
    throw {
      message: error.response?.data?.error || 'Failed to mark invoice as viewed',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get invoice statistics
 */
export const getInvoiceStats = async (params = {}) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/stats`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching invoice stats:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch invoice stats',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Delete invoice (soft delete)
 */
export const deleteInvoice = async (id) => {
  try {
    const response = await apiClient.delete(`${BASE_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting invoice:', error);
    throw {
      message: error.response?.data?.error || 'Failed to delete invoice',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Update overdue invoices (admin/cron action)
 */
export const updateOverdueInvoices = async () => {
  try {
    const response = await apiClient.post(`${BASE_URL}/check-overdue`);
    return response.data;
  } catch (error) {
    console.error('Error updating overdue invoices:', error);
    throw {
      message: error.response?.data?.error || 'Failed to update overdue invoices',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== PDF GENERATION =====

/**
 * Generate PDF for invoice
 */
export const generateInvoicePDF = async (id) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/generate-pdf`);
    return response.data;
  } catch (error) {
    console.error('Error generating invoice PDF:', error);
    throw {
      message: error.response?.data?.error || 'Failed to generate invoice PDF',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get invoice by order ID
 */
export const getInvoiceByOrderId = async (orderId) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/order/${orderId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching invoice by order ID:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch invoice',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Generate receipt PDF for payment
 */
export const generateReceiptPDF = async (paymentId) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/payments/${paymentId}/generate-receipt`);
    return response.data;
  } catch (error) {
    console.error('Error generating receipt PDF:', error);
    throw {
      message: error.response?.data?.error || 'Failed to generate receipt PDF',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== EMAIL INTEGRATION =====

/**
 * Send invoice via email
 */
export const sendInvoiceEmail = async (id) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/send-email`);
    return response.data;
  } catch (error) {
    console.error('Error sending invoice email:', error);
    throw {
      message: error.response?.data?.error || 'Failed to send invoice email',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Send payment reminder email
 */
export const sendReminderEmail = async (id, reminderType = 'payment_reminder') => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/send-reminder-email`, {
      reminder_type: reminderType
    });
    return response.data;
  } catch (error) {
    console.error('Error sending reminder email:', error);
    throw {
      message: error.response?.data?.error || 'Failed to send reminder email',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Test email connection
 */
export const testEmailConnection = async () => {
  try {
    const response = await apiClient.post(`${BASE_URL}/test-email`);
    return response.data;
  } catch (error) {
    console.error('Error testing email connection:', error);
    throw {
      message: error.response?.data?.error || 'Failed to test email connection',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get email configuration
 */
export const getEmailConfig = async () => {
  try {
    const response = await apiClient.get(`${BASE_URL}/email-config`);
    return response.data;
  } catch (error) {
    console.error('Error fetching email config:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch email configuration',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Update email configuration
 */
export const updateEmailConfig = async (config) => {
  try {
    const response = await apiClient.put(`${BASE_URL}/email-config`, config);
    return response.data;
  } catch (error) {
    console.error('Error updating email config:', error);
    throw {
      message: error.response?.data?.error || 'Failed to update email configuration',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

export default {
  getAllInvoices,
  getInvoiceById,
  createInvoice,
  updateInvoiceStatus,
  recordInvoicePayment,
  sendPaymentReminder,
  markInvoiceAsViewed,
  getInvoiceStats,
  deleteInvoice,
  updateOverdueInvoices,
  generateInvoicePDF,
  generateReceiptPDF,
  sendInvoiceEmail,
  sendReminderEmail,
  testEmailConnection,
  getEmailConfig,
  updateEmailConfig
};
